import { useEffect, useRef, useState } from "react";
import { Bell, Check, Trash2, X, CreditCard, AlertCircle, Megaphone, Sparkles, Keyboard, Monitor, Mic, Receipt } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const TYPE_ICON = {
  system: Sparkles,
  billing: CreditCard,
  usage: AlertCircle,
  transcription: Mic,
  payment: Receipt,
  announcement: Megaphone,
  extension: Keyboard,
  desktop: Monitor,
};
const TYPE_COLOR = {
  system: "text-brand-purple",
  billing: "text-emerald-600",
  usage: "text-amber-600",
  transcription: "text-brand-blue",
  payment: "text-emerald-600",
  announcement: "text-brand-pink",
  extension: "text-brand-blue",
  desktop: "text-brand-purple",
};

function timeAgo(iso) {
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
  } catch { return ""; }
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); const t = setInterval(load, 45000); return () => clearInterval(t); }, []);
  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const markRead = async (id) => {
    await api.post(`/notifications/${id}/read`);
    setItems((arr) => arr.map((n) => n.id === id ? { ...n, read: true } : n));
    setUnread((u) => Math.max(0, u - 1));
  };
  const markAllRead = async () => {
    await api.post("/notifications/mark-all-read");
    setItems((arr) => arr.map((n) => ({ ...n, read: true })));
    setUnread(0);
    toast.success("All caught up");
  };
  const clearAll = async () => {
    await api.delete("/notifications");
    setItems([]); setUnread(0);
    toast.success("Cleared");
  };

  return (
    <div ref={ref} className="relative">
      <button
        data-testid="notif-bell-btn"
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 rounded-full hover:bg-brand-surface text-brand-text"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span data-testid="notif-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-gradient-warm text-white text-[10px] font-bold flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="notif-dropdown" className="absolute right-0 mt-2 w-[360px] max-w-[92vw] bg-white border border-brand-borderc rounded-2xl shadow-soft z-50 overflow-hidden">
          <div className="px-4 py-3 flex items-center justify-between border-b border-brand-borderc">
            <div className="font-semibold text-brand-navy">Notifications</div>
            <div className="flex items-center gap-1">
              <button data-testid="notif-mark-all" onClick={markAllRead} className="text-[11px] px-2 py-1 rounded-full bg-brand-surface hover:bg-brand-borderc inline-flex items-center gap-1"><Check className="w-3 h-3" />Mark all</button>
              <button data-testid="notif-clear-all" onClick={clearAll} className="text-[11px] px-2 py-1 rounded-full bg-brand-surface hover:bg-brand-borderc inline-flex items-center gap-1"><Trash2 className="w-3 h-3" />Clear</button>
            </div>
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {items.length === 0 ? (
              <div className="text-center px-6 py-10 text-sm text-brand-muted">
                <Bell className="w-8 h-8 mx-auto text-brand-purple" />
                <div className="mt-2 font-medium text-brand-text">You're all caught up</div>
                <div className="mt-1 text-xs">New activity will show up here.</div>
              </div>
            ) : items.map((n) => {
              const Icon = TYPE_ICON[n.type] || Sparkles;
              const color = TYPE_COLOR[n.type] || "text-brand-purple";
              return (
                <div
                  key={n.id}
                  data-testid={`notif-row-${n.id}`}
                  className={`px-4 py-3 border-b border-brand-borderc last:border-0 flex gap-3 cursor-pointer hover:bg-brand-surface/60 ${!n.read ? "bg-[#F4F2FF]" : ""}`}
                  onClick={() => { if (!n.read) markRead(n.id); if (n.link) window.location.href = n.link; }}
                >
                  <div className={`mt-0.5 w-8 h-8 rounded-full bg-brand-surface flex items-center justify-center shrink-0 ${color}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between gap-2">
                      <div className="font-semibold text-sm text-brand-navy truncate">{n.title}</div>
                      <div className="text-[10px] text-brand-muted shrink-0">{timeAgo(n.created_at)}</div>
                    </div>
                    <div className="text-xs text-brand-text mt-0.5 line-clamp-2">{n.body}</div>
                  </div>
                  {!n.read && <span className="w-2 h-2 mt-2 rounded-full bg-gradient-primary shrink-0" />}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
